// M040 C2: dark: removed, dark/setDark state removed, TopBar dark props removed, colors → tokens
// data-fit LLC · DecisionFlow · TenantAdmin Stage
// TenantAdminShell.jsx · DDL: R13 · API: v1.22 · Session 159
// Auth: useAuth() via AuthContext — ARCH-2 compliant
// Key constraints: tenant_super_admin shell separation; SCREEN_ORDER CustomEvent navigation; no token/user props
// OI-TSA-SHELL-CSS: Full Tailwind rewrite — BEM classes removed, shared primitives adopted
// Open Items: none

import { useCallback, useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Settings2, ShieldCheck, ShieldOff, UserRound } from 'lucide-react'
import { useAuth } from '../../auth/AuthContext.jsx'
import TopBar from '../shared/TopBar.jsx'
import UserManager from './UserManager'
import RoleAssignmentManager from './RoleAssignmentManager'
import AccessScopeConfig from './AccessScopeConfig'
import KillSwitchManager from './KillSwitchManager'

export const SCREEN_ORDER = ['UserManager', 'RoleAssignmentManager', 'AccessScopeConfig', 'KillSwitchManager']

const AI_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
}

const SCREEN_META = {
  UserManager:          { label: 'Users',                  icon: UserRound  },
  RoleAssignmentManager:{ label: 'Role Assignments',       icon: ShieldCheck },
  AccessScopeConfig:    { label: 'Access & Tenant Config', icon: Settings2  },
  KillSwitchManager:    { label: 'Agent Kill Switch',      icon: ShieldOff  },
}

export default function TenantAdminShell() {
  const { user } = useAuth()

  const [mobileOpen, setMobileOpen] = useState(false)

  const [aiStatus, setAiStatus] = useState(AI_STATUS.IDLE)
  const [aiResponse, setAiResponse] = useState(null)
  const [currentScreen, setCurrentScreen] = useState(SCREEN_ORDER[0])

  const onNavigate = useCallback((screenName) => {
    const targetIndex = SCREEN_ORDER.indexOf(screenName)
    if (targetIndex === -1) {
      console.warn(`Invalid TenantAdmin screen requested: ${screenName}`)
      return
    }
    window.dispatchEvent(
      new CustomEvent('decisionflow', {
        detail: { screen: SCREEN_ORDER[targetIndex] },
      }),
    )
  }, [])

  useEffect(() => {
    function handleDecisionFlowNavigation(event) {
      const requestedScreen = event?.detail?.screen
      if (!SCREEN_ORDER.includes(requestedScreen)) {
        console.warn(`Invalid TenantAdmin screen requested: ${requestedScreen}`)
        return
      }
      setCurrentScreen(requestedScreen)
    }
    window.addEventListener('decisionflow', handleDecisionFlowNavigation)
    return () => window.removeEventListener('decisionflow', handleDecisionFlowNavigation)
  }, [])

  useEffect(() => {
    if (SCREEN_ORDER.includes(currentScreen)) return
    console.warn(`Invalid TenantAdmin currentScreen resolved: ${currentScreen}`)
    setCurrentScreen(SCREEN_ORDER[0])
  }, [currentScreen])

  const currentScreenIndex = useMemo(() => {
    const index = SCREEN_ORDER.indexOf(currentScreen)
    return index === -1 ? 0 : index
  }, [currentScreen])

  const goPrevious = useCallback(() => {
    const nextIndex = Math.max(currentScreenIndex - 1, 0)
    onNavigate(SCREEN_ORDER[nextIndex])
  }, [currentScreenIndex, onNavigate])

  const goNext = useCallback(() => {
    const nextIndex = Math.min(currentScreenIndex + 1, SCREEN_ORDER.length - 1)
    onNavigate(SCREEN_ORDER[nextIndex])
  }, [currentScreenIndex, onNavigate])

  function renderCurrentScreen() {
    const sharedProps = { aiStatus, aiResponse, currentScreen, onNavigate }

    if (currentScreen === SCREEN_ORDER[0]) return <UserManager {...sharedProps} />
    if (currentScreen === SCREEN_ORDER[1]) return <RoleAssignmentManager {...sharedProps} />
    if (currentScreen === SCREEN_ORDER[2]) return <AccessScopeConfig {...sharedProps} />
    if (currentScreen === SCREEN_ORDER[3]) return <KillSwitchManager />
    return null
  }

  const userName = user?.full_name ?? user?.email ?? null
  const userRole = user?.user_type ?? 'tenant_super_admin'

  return (
    <div className="min-h-screen bg-[rgb(var(--color-bg))] text-[rgb(var(--color-text))] ">
      <TopBar
        userName={userName}
        userRole={userRole}
        userRoleLabel="Tenant Super Admin"
        mode="prod"
        onMenuOpen={() => setMobileOpen(true)}
        copilotOpen={false}
        onCopilotToggle={() => {}}
      />

      <div className="flex min-h-[calc(100vh-64px)]">
        {/* Left nav */}
        <aside
          className={`
            fixed inset-y-0 left-0 z-40 flex w-56 flex-col border-r border-slate-200
            bg-white pt-16             transition-transform duration-200
            ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
            lg:static lg:translate-x-0 lg:pt-0
          `}
        >
          {/* Mobile close overlay target — clicking outside closes */}
          {mobileOpen && (
            <div
              className="fixed inset-0 z-30 bg-[rgb(var(--color-text))]/50 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
          )}

          <div className="flex flex-col gap-1 p-3">
            <p className="mb-1 px-2 text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))] ">
              Tenant Administration
            </p>

            {SCREEN_ORDER.map((screenName, index) => {
              const meta = SCREEN_META[screenName]
              const Icon = meta.icon
              const isActive = screenName === currentScreen

              return (
                <button
                  key={screenName}
                  type="button"
                  onClick={() => {
                    onNavigate(screenName)
                    setMobileOpen(false)
                  }}
                  className={`
                    flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-tok-sm font-medium transition
                    ${isActive
                      ? 'bg-[rgb(var(--color-primary))] text-white '
                      : 'text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] '
                    }
                  `}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span>{meta.label}</span>
                  <span className={`ml-auto text-xs ${isActive ? 'text-white/60' : 'text-[rgb(var(--color-text-muted))]'}`}>
                    {index + 1}
                  </span>
                </button>
              )
            })}
          </div>

          {/* Step controls */}
          <div className="mt-auto border-t border-[rgb(var(--color-border))] p-3">
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={goPrevious}
                disabled={currentScreenIndex === 0}
                className="flex-1 rounded-xl border border-[rgb(var(--color-border))] py-1.5 text-tok-xs font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:cursor-not-allowed disabled:opacity-40"
              >
                ← Prev
              </button>
              <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                {currentScreenIndex + 1}/{SCREEN_ORDER.length}
              </span>
              <button
                type="button"
                onClick={goNext}
                disabled={currentScreenIndex === SCREEN_ORDER.length - 1}
                className="flex-1 rounded-xl border border-[rgb(var(--color-border))] py-1.5 text-tok-xs font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="min-w-0 flex-1 p-4 lg:p-6">
          {/* Screen header */}
          <div className="mb-6">
            <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))] ">
              Tenant Administration
            </p>
            <h1 className="mt-0.5 text-tok-xl font-bold text-[rgb(var(--color-text))]">
              Access Management
            </h1>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              Manage tenant users, role assignments, and enterprise access scope.
            </p>
          </div>

          {/* Actor card */}
          <div className="mb-6 flex items-center gap-3 rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3">
            <UserRound className="h-5 w-5 shrink-0 text-[rgb(var(--color-text-muted))]" />
            <div className="min-w-0">
              <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Signed in as</p>
              <p className="truncate text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                {user?.email ?? 'Authenticated user'}
              </p>
            </div>
            <div className="ml-auto">
              <span className="inline-flex items-center rounded-full bg-[rgb(var(--color-surface-muted))] px-2.5 py-1 text-tok-xs font-semibold text-[rgb(var(--color-text))] ring-1 ring-[rgb(var(--color-border))]">
                {user?.user_type ?? 'tenant_super_admin'}
              </span>
            </div>
          </div>

          {/* AI status banner */}
          {aiStatus !== AI_STATUS.IDLE && (
            <div className={`mb-4 rounded-xl px-4 py-3 text-tok-sm font-medium
              ${aiStatus === AI_STATUS.ERROR
                ? 'bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]'
                : aiStatus === AI_STATUS.READY
                  ? 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]'
                  : 'bg-[rgb(var(--color-info))]/10 text-[rgb(var(--color-info))]'
              }`}
            >
              <strong>AI:</strong> {aiResponse ?? aiStatus}
            </div>
          )}

          {/* Animated screen content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentScreen}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
            >
              {renderCurrentScreen()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  )
}
